import { useNotification } from 'admin';
import { isFieldErrorResponse, type PatchedShipmentUpdate, useApiLogisticsShipmentsRetrieve } from 'api';
import { Form, type SubmitHandler, useForm } from 'form';
import { type ChangeEvent, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, Grid, Page, Typography } from 'ui';

import { ShipmentUpdateFieldsSection } from '~/logistics/components/ShipmentUpdateFieldsSection';

import { BookingConfirmationDoc } from '../components/BookingConfirmationDoc';
import { ClearOverridesSection } from '../components/ClearOverridesSection';
import { LoadsSection } from '../components/LoadsSection';
import { useUpdateShipment } from '../hooks/useUpdateShipment';

export const ShipmentUpdate = () => {
  const { shipmentName } = useParams();
  const { open } = useNotification();

  if (!shipmentName) {
    throw new Error('Shipment name is not set');
  }

  const { data: shipmentData, isLoading, refetch } = useApiLogisticsShipmentsRetrieve(shipmentName);

  const initializedRef = useRef(false);

  const form = useForm<PatchedShipmentUpdate>();
  const {
    formState: { isDirty },
    getValues,
    setError,
    setValue,
    reset,
    resetField,
  } = form;

  const { updateShipment, isPending, error } = useUpdateShipment();

  useEffect(() => {
    if (shipmentData && !initializedRef.current) {
      const freightForwarderReference = Array.isArray(shipmentData.freight_forwarder_reference)
        ? shipmentData.freight_forwarder_reference.join(', ')
        : '';

      reset({
        ...shipmentData,
        booking_confirmation: null,
        clear_loads_overrides: false,
        freight_forwarder_reference: freightForwarderReference,
        // @ts-expect-error missing form schema
        load_ids: shipmentData.loads,
      });
      // set as initialised to prevent subsequent resetting
      // of the form when users go from and to tab/window
      initializedRef.current = true;
    }
  }, [shipmentData, reset]);

  const onSubmit: SubmitHandler<PatchedShipmentUpdate> = async (data) => {
    try {
      // @ts-expect-error missing form schema
      const bookingConfirmation = getValues('booking_confirmation');
      const load_ids = (data.load_ids as unknown as { id: number; name: string }[]).map(({ id }) => id) as number[];
      // @ts-expect-error missing form schema
      await updateShipment(bookingConfirmation, { ...data, load_ids }, shipmentName);
      await refetch();
      setValue('clear_loads_overrides', false);
      // @ts-expect-error missing form schema
      resetField('booking_confirmation');
      open?.({ message: 'Shipment updated successfully', type: 'success' });
    } catch {
      if (isFieldErrorResponse(error) && error.response?.data) {
        Object.entries(error.response.data).forEach(([field, message]) => {
          // @ts-expect-error missing form schema
          setError(field, { message });
        });
      }
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // @ts-expect-error missing form schema
      setValue('booking_confirmation', file, { shouldDirty: true });
    }
  };

  if (isLoading) {
    return <Typography>Loading shipment data...</Typography>;
  }

  return (
    <Page>
      <Form {...form} onSubmit={onSubmit}>
        {error ? (
          <Box paddingY={2}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : null}
        <Box mb={3}>
          <Typography variant="h5" fontWeight="bold">
            Update Shipment: {shipmentName}
          </Typography>
        </Box>
        <BookingConfirmationDoc shipmentData={shipmentData} handleFileChange={handleFileChange} />
        <LoadsSection shipmentName={shipmentName} />
        <ShipmentUpdateFieldsSection defaultValues={shipmentData} />
        <ClearOverridesSection />
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" paddingY={3}>
            <Button type="submit" size="large" loading={isPending} disabled={!isDirty}>
              Update Shipment
            </Button>
          </Box>
        </Grid>
      </Form>
    </Page>
  );
};
